<template>
    <div class="user-group">
        <vrm-header></vrm-header>
        <vrm-title>
            <template v-slot:back-link>
                <a href="/teams" class="vrm-back-link">
                    <span class="vrm-back-link__icon sg-vrm-icon_048_arrow-left-16px" />
                    {{ $t('general.back', { i18nFallback: 'Back' }) }}
                </a>
            </template>
            <template v-slot:page-title>
                <h1 class="vrm-page-title">
                    <span>
                        {{ team ? team.name : $t('core.loading', { i18nFallback: 'Loading...' }) }}
                    </span>
                </h1>
            </template>
        </vrm-title>
        <div v-if="team" class="container">
            <div class="team__header">
                <span>
                    {{ $t('teams.owner_name', { i18nFallback: 'Owner: {name}', name: ownerName }) }}
                    <span v-if="localUserId === team.idOwner">
                        {{ $t('teams.you', { i18nFallback: '(you)' }) }}
                    </span>
                    <br />
                    <span>
                        {{ $t('teams.admin', { i18nFallback: 'Admin' }) + ': ' }}
                    </span>
                    <span v-if="admins.length === 0">
                        {{ $t('teams.none', { i18nFallback: 'None' }) }}
                    </span>
                    <span v-else>
                        <span v-for="(admin, index) in admins" :key="index">
                            <span>{{ admin.name }}</span>
                            <span v-if="localUserId === admin.id">
                                {{ $t('teams.you', { i18nFallback: '(you)' }) }}
                            </span>
                            <span v-if="index < admins.length - 1">,&nbsp;</span>
                        </span>
                    </span>
                </span>
                <div class="sg-btn__container--align-right user-group__action-buttons">
                    <vrm-icon-button
                        v-if="showVenusGrafanaButton"
                        v-tooltip="'Venus-Grafana'"
                        :active="team.hasVenusGrafanaInstance"
                        with-background
                        vrm-dropdown-trigger="venus-grafana"
                        icon-class="sg-vrm-icon_129_grafana-16px"
                    ></vrm-icon-button>
                    <button
                        v-if="canMakeChanges"
                        type="button"
                        class="sg-btn sg-blue"
                        @click.prevent="editTeam"
                    >
                        {{ $t('teams.edit_team', { i18nFallback: 'Edit Team' }) }}
                    </button>
                </div>
            </div>
            <vrm-dropdown-popup
                :items="venusGrafanaDropdownOptions"
                dropdown-trigger="venus-grafana"
            />
            <h2>{{ $t('teams.members', { i18nFallback: 'Members' }) }}</h2>
            <vrm-scroll-fade>
                <vrm-foldable-table :items="teamMembers" key-by="idUser">
                    <template v-slot:thead>
                        <tr>
                            <th>{{ $t('teams.user', { i18nFallback: 'User' }) }}</th>
                            <th>{{ $t('common.email', { i18nFallback: 'Email' }) }}</th>
                            <th class="table-fixed-width-access-level">
                                {{ $t('teams.access_levels', { i18nFallback: 'Team roles' }) }}
                                <span
                                    class="sg-vrm-icon_061_info-round-16px vrm-table__icon--link"
                                    @click="showTeamMembersRoleGuideModal"
                                ></span>
                            </th>
                            <th />
                        </tr>
                    </template>
                    <template v-slot:main="{ item: member }">
                        <td class="vrm-table__nowrap">
                            <vrm-avatar :user="member" admin-key="isAdmin" />
                            {{ member.name }}
                            <span v-if="localUserId === member.idUser">
                                {{ $t('teams.you', { i18nFallback: '(you)' }) }}
                            </span>
                        </td>
                        <td class="sg-color-blue">
                            <a :href="'mailto:' + member.email">{{ member.email }}</a>
                        </td>
                        <td v-if="team.idOwner == member.idUser">
                            {{ $t('teams.team_owner', { i18nFallback: 'Team owner' }) }}
                        </td>
                        <td v-else class="ui-select--simple">
                            <vrm-dropdown
                                v-model="member.userOnTeamsAccessLevel"
                                :options="userOnTeamsAccessLevelsOptions"
                                :disabled="!canMakeChanges"
                                :append-to-body="true"
                                :dropdown-classes="'ui-select--simple'"
                            />
                        </td>
                        <td class="vrm-table__actions">
                            <a
                                v-if="canRemoveMember(member.idUser)"
                                class="vrm-table__icon--link"
                                @click.prevent="removeMember(member.idUser)"
                            >
                                <i class="sg-vrm-icon_055_cross-16px"></i>
                            </a>
                        </td>
                    </template>
                    <template v-slot:noEntries>
                        {{
                            $t('teams.no_members', {
                                i18nFallback:
                                    'No members/users were found for this team. You can add a new member by clicking on the "Add Members" button.',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
            <div class="sg-btn__container sg-btn__container--align-right">
                <button
                    v-if="canMakeChanges"
                    type="button"
                    class="sg-btn sg-blue"
                    @click="linkMembersPopup"
                >
                    {{ $t('teams.add_members', { i18nFallback: 'Add members' }) }}
                </button>
            </div>
            <h2>
                {{
                    $t('teams.linked_sites_and_groups', {
                        i18nFallback: 'Linked installations and groups',
                    })
                }}
            </h2>
            <vrm-scroll-fade>
                <vrm-foldable-table :items="combinedSiteList" key-by="key">
                    <template v-slot:thead>
                        <tr>
                            <th>
                                {{
                                    $t('teams.sites_and_groups', {
                                        i18nFallback: 'Installations & Groups',
                                    })
                                }}
                            </th>
                            <th>
                                {{ $t('teams.installations', { i18nFallback: 'Installations' }) }}
                            </th>
                            <th>
                                {{ $t('users.role', { i18nFallback: 'Role' }) }}
                                <span
                                    class="sg-vrm-icon_061_info-round-16px vrm-table__icon--link"
                                    @click="showRoleGuideModal"
                                ></span>
                            </th>
                            <th />
                        </tr>
                    </template>
                    <template v-slot:main="{ item, toggle, expanded }">
                        <template v-if="item.isGroup">
                            <td>
                                {{ item.title }}
                                <span class="vrm-table__sub-text">
                                    {{
                                        $t('teams.owner_name', {
                                            i18nFallback: 'Owner: {name}',
                                            name: item.group.adminName,
                                        })
                                    }}
                                </span>
                            </td>
                        </template>
                        <template v-else>
                            <td>
                                <span
                                    :class="convertDeviceIcon(item.site.device_icon)"
                                    class="vrm-table__icon"
                                />
                                {{ item.title }}
                            </td>
                        </template>
                        <td>{{ item.isGroup ? item.group.siteCount : '' }}</td>
                        <td class="ui-select--simple">
                            <vrm-dropdown
                                v-model="item.accessLevelOption"
                                data-cy="access-level"
                                :options="accessLevelDropdownOptions"
                                :disabled="
                                    item.isGroup ? !canMakeChanges : !canMakeSiteChanges(item)
                                "
                                :append-to-body="true"
                                :dropdown-classes="'ui-select--simple'"
                            />
                        </td>
                        <td class="vrm-table__actions">
                            <i
                                v-if="item.isGroup || canMakeSiteChanges(item)"
                                class="vrm-table__icon--link"
                                :class="
                                    expanded
                                        ? 'sg-vrm-icon_059_min-round-16px'
                                        : 'sg-vrm-icon_058_plus-round-16px'
                                "
                                @click="toggle"
                            />
                        </td>
                    </template>
                    <template v-slot:extra="{ item }">
                        <td colspan="4">
                            <div v-if="item.isGroup">
                                <h4>
                                    {{
                                        $t('teams.installations', {
                                            i18nFallback: 'Installations',
                                        })
                                    }}
                                </h4>
                                <p
                                    v-for="site in item.group.sites"
                                    :key="site.idSite"
                                    class="vrm-table__item_secondary"
                                >
                                    <span
                                        :class="convertDeviceIcon(site.device_icon)"
                                        class="vrm-table__icon"
                                    />
                                    {{ site.description }}
                                </p>
                            </div>
                            <div
                                v-if="canMakeChanges || (!item.isGroup && canMakeSiteChanges(item))"
                                class="sg-spread"
                            >
                                <template v-if="item.isGroup">
                                    <button
                                        type="button"
                                        :disabled="busy"
                                        class="sg-btn sg-site"
                                        @click.prevent="unlinkSiteGroup(item.group.idSiteGroup)"
                                    >
                                        {{
                                            $t('teams.unlink_site_group', {
                                                i18nFallback: 'Unlink installation group',
                                            })
                                        }}
                                    </button>
                                    <router-link
                                        type="button"
                                        :to="{
                                            name: 'base.installation-group',
                                            params: { id: item.group.idSiteGroup },
                                        }"
                                        class="sg-btn sg-blue"
                                    >
                                        {{
                                            $t('teams.view_site_group', {
                                                i18nFallback: 'View installation group',
                                            })
                                        }}
                                    </router-link>
                                </template>
                                <template v-else>
                                    <button
                                        type="button"
                                        :disabled="busy"
                                        class="sg-btn sg-site"
                                        @click.prevent="unlinkSite(item.site.idSite)"
                                    >
                                        {{
                                            $t('teams.unlink_site', {
                                                i18nFallback: 'Unlink installation',
                                            })
                                        }}
                                    </button>
                                    <router-link
                                        type="button"
                                        :to="{
                                            name: 'base.installation.settings.users',
                                            params: { id: item.site.idSite },
                                        }"
                                        class="sg-btn sg-blue"
                                    >
                                        {{
                                            $t('teams.view_site', {
                                                i18nFallback: 'View installation',
                                            })
                                        }}
                                    </router-link>
                                </template>
                            </div>
                        </td>
                    </template>
                    <template v-if="canMakeChanges" v-slot:noEntries>
                        {{
                            $t('teams.no_installations_admin', {
                                i18nFallback:
                                    'No installations are link to this team. You can link a installation by clicking on the "Link Installations" button.',
                            })
                        }}
                    </template>
                    <template v-else v-slot:noEntries>
                        {{
                            $t('teams.no_installations_non_admin', {
                                i18nFallback:
                                    'No installations are linked to this team. Only admins of the team can link installations.',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
            <div v-if="canMakeChanges" class="sg-btn__container--align-right">
                <button type="button" class="sg-btn sg-site" @click.prevent="linkSiteGroupsPopup">
                    {{ $t('teams.link_site_groups', { i18nFallback: 'Link installation groups' }) }}
                </button>
                <button type="button" class="sg-btn sg-blue" @click.prevent="linkSitesPopup">
                    {{ $t('teams.link_sites', { i18nFallback: 'Link installations' }) }}
                </button>
            </div>

            <!-- Invites -->
            <h2>
                {{ $t('users.invitations', { i18nFallback: 'Pending invitations' }) }}
            </h2>
            <vrm-scroll-fade>
                <vrm-foldable-table :items="team.invites" key-by="email">
                    <template v-slot:thead>
                        <tr>
                            <th>
                                {{ $t('users.invited_user', { i18nFallback: 'Invited user' }) }}
                            </th>
                            <th>{{ $t('users.email', { i18nFallback: 'Email' }) }}</th>
                            <th />
                        </tr>
                    </template>
                    <template v-slot:main="{ item: user }">
                        <td class="vrm-table__nowrap">
                            <vrm-avatar :user="user" />
                            {{ user.name }}
                        </td>
                        <td class="sg-color-blue">
                            <a :href="'mailto:' + user.email">{{ user.email }}</a>
                        </td>
                        <td class="vrm-table__actions">
                            <a
                                v-if="canMakeChanges"
                                class="vrm-table__icon--link sg-vrm-icon_055_cross-16px"
                                @click.prevent="removeInvite(user.email)"
                            />
                        </td>
                    </template>
                    <template v-if="canMakeChanges" v-slot:noEntries>
                        {{
                            $t('teams.no_pending_admin', {
                                i18nFallback:
                                    'No pending invitations for this team. You can add a new member by clicking on the "Add Members" button.',
                            })
                        }}
                    </template>
                    <template v-else v-slot:noEntries>
                        {{
                            $t('teams.no_pending_non_admin', {
                                i18nFallback:
                                    'No pending invitations for this team. Only admins of the team can add new members',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
            <div v-if="canDelete">
                <h2>
                    {{ $t('teams.delete_team', { i18nFallback: 'Delete team' }) }}
                </h2>
                <p>{{ $t('teams.delete_team_explainer') }}</p>
                <div class="sg-btn__container sg-btn__container--align-left">
                    <button
                        type="button"
                        class="sg-btn sg-red user-group-page-del-btn"
                        @click.prevent="onDelete"
                    >
                        {{ $t('general.delete', { i18nFallback: 'Delete' }) }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { Vue, Component } from 'vue-property-decorator';
import VrmIconButton from '@/vue/components/molecules/VrmIconButton';
import VrmHeaderSearchPanel from '@/vue/components/molecules/VrmHeaderSearchPanel';
import VrmAvatar from '@/vue/components/shared/VrmAvatar';
import VrmToggleButton from '@/vue/components/shared/toggles/VrmToggleButton';
import VrmHeader from '@/vue/components/layout/VrmHeader';
import VrmTitle from '@/vue/components/layout/VrmTitle';
import VrmFoldableTable from '@/vue/components/molecules/VrmFoldableTable';
import VrmScrollFade from '@/vue/components/shared/VrmScrollFade';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import UserGroupLinkInstallationModal from '@/vue/components/modals/UserGroupLinkInstallationModal';
import UserGroupSettingsModal from '@/vue/components/modals/UserGroupSettingsModal';
import UserGroupLinkUsersModal from '@/vue/components/modals/UserGroupLinkUsersModal';
import UserGroupLinkInstallationGroupsModal from '@/vue/components/modals/UserGroupLinkInstallationGroupsModal';
import { NOTIFICATION_TYPE_SUCCESS } from '@/vue/store/modules/notifications';
import ConfirmDeletionModal from '@/vue/components/modals/ConfirmDeletionModal';
import UserRolesGuideModal from '@/vue/components/modals/UserRolesGuideModal';
import TeamMembersRolesGuideModal from '@/vue/components/modals/TeamMembersRolesGuideModal';
import {
    accessLevelDropdownOptions,
    findAccessLevelOption,
    userOnTeamsAccessLevelsOptions,
    findUserOnTeamsAccessLevelOption,
} from '@/vue/store/modules/installation-access-level';
import VrmDropdownPopup from '@/vue/components/shared/VrmDropdownPopup.vue';

@Component({
    components: {
        VrmDropdownPopup,
        VrmDropdown,
        VrmIconButton,
        VrmHeaderSearchPanel,
        VrmAvatar,
        VrmToggleButton,
        VrmHeader,
        VrmFoldableTable,
        VrmScrollFade,
        VrmTitle,
    },
})
@AngularRoute('base.user-group', '/teams/{id:int}', { slot: 'main' })
@AngularLocaleBundle('core')
@AngularLocaleBundle('nav')
@AngularLocaleBundle('teams')
@AngularLocaleBundle('users')
@AngularLocaleBundle('general')
export default class UserGroup extends Vue {
    /** @type UserGroupsService */
    @ngInject()
    UserGroupsService;
    @ngInject()
    UserService;
    @ngInject()
    $rootScope;
    @ngInject()
    VrmModalService;
    @ngInject()
    $filter;
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    ApiService;

    DROPDOWN_ITEM_DELETE_GRAFANA_INSTANCE = {
        text: this.$t('venus_grafana.delete_instance', {
            i18nFallback: 'Delete Venus-Grafana instance',
        }),
        icon_class: 'sg-vrm-icon_131_stop-16px',
        action: this.deleteVenusGrafana,
    };
    DROPDOWN_ITEM_GO_TO_GRAFANA_INSTANCE = {
        text: this.$t('venus_grafana.go_to_instance', {
            i18nFallback: 'Go to Venus-Grafana instance',
        }),
        icon_class: 'sg-vrm-icon_132_open_link-16px',
        action: this.openVenusGrafana,
    };
    DROPDOWN_ITEM_CREATE_GRAFANA_INSTANCE = {
        text: this.$t('venus_grafana.create_instance', {
            i18nFallback: 'Create Venus-Grafana instance',
        }),
        icon_class: 'sg-vrm-icon_130_play-16px',
        action: this.createVenusGrafana,
    };

    venusGrafanaLoading = false;

    accessLevelDropdownOptions = accessLevelDropdownOptions;

    userOnTeamsAccessLevelsOptions = userOnTeamsAccessLevelsOptions;

    convertDeviceIcon(iconCode) {
        return this.$filter('vrmDeviceIcon')(iconCode);
    }

    setViewTitle() {
        if (this.team && this.team.name && this.$rootScope) {
            this.$rootScope.viewTitle = this.team.name;
        }
    }

    clearViewTitle() {
        if (this.$rootScope && this.$rootScope.viewTitle) {
            this.$rootScope.viewTitle = null;
        }
    }

    beforeDestroy() {
        this.clearViewTitle();
    }

    async created() {
        this.clearViewTitle();
        try {
            this.loading = true;
            await this.loadGroups();
        } finally {
            this.loading = false;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const welcome = urlParams.get('welcome');
        if (welcome) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('teams.welcome', {
                    i18nFallback: 'Welcome to your new team!',
                }),
            });
        }
    }

    async loadGroups() {
        return await this.UserGroupsService.loadUserGroups().then(() => {
            if (!this.team) {
                this.$router.replace('base.user-groups');
            } else {
                this.setViewTitle();
            }
        });
    }

    get team() {
        return this.$store.state.UserGroupsService.groups.find(
            group => group.idUserGroup == this.$route.params.id,
        );
    }

    get teamMembers() {
        const self = this;
        return this.team.members.map(member => {
            return {
                ...member,
                get userOnTeamsAccessLevel() {
                    return findUserOnTeamsAccessLevelOption(member.isAdmin);
                },
                set userOnTeamsAccessLevel(option) {
                    self.syncMembersControl(member, option.isAdmin);
                },
            };
        });
    }

    get admins() {
        return this.team.members.filter(member => member.isAdmin === 1);
    }

    editTeam() {
        if (this.team) {
            this.VrmModalService.openVueModal(UserGroupSettingsModal, {
                team: {
                    ...this.team,
                    admins: this.admins,
                },
                idUser: this.localUserId,
            });
        }
    }

    createVenusGrafana() {
        if (this.venusGrafanaLoading) return;
        this.venusGrafanaLoading = true;

        const team = this.team;
        this.ApiService.venusGrafana(team.idUserGroup, 'POST')
            .then(response => {
                if (response.data.success) {
                    this.UserGroupsService.updateCachedUserGroupFields(team.idUserGroup, {
                        hasVenusGrafanaInstance: true,
                    });

                    this.vrmNotificationsService.add({
                        type: NOTIFICATION_TYPE_SUCCESS,
                        message:
                            'Deploying venus-grafana for: ' +
                            team.name +
                            '. This can take a few minutes',
                    });
                }
            })
            .finally(() => {
                this.venusGrafanaLoading = false;
            });
    }

    openVenusGrafana() {
        if (this.venusGrafanaLoading) return;
        this.venusGrafanaLoading = true;

        let newWindow = window.open();
        this.ApiService.venusGrafana(this.team.idUserGroup, 'GET')
            .then(function(response) {
                if (response.data.success && response.data.url) {
                    newWindow.location = response.data.url;
                } else {
                    newWindow.close();
                }
            })
            .catch(function() {
                newWindow.close();
            })
            .finally(() => {
                this.venusGrafanaLoading = false;
            });
    }

    deleteVenusGrafana() {
        if (this.venusGrafanaLoading) return;

        this.VrmModalService.openConfirmModal(
            this.$rootScope,
            'delete_venus_grafana',
            confirmed => {
                if (confirmed) {
                    this.venusGrafanaLoading = true;
                    const team = this.team;
                    this.ApiService.venusGrafana(team.idUserGroup, 'DELETE')
                        .then(response => {
                            if (response.data.success) {
                                this.UserGroupsService.updateCachedUserGroupFields(
                                    team.idUserGroup,
                                    {
                                        hasVenusGrafanaInstance: false,
                                    },
                                );

                                this.vrmNotificationsService.add({
                                    type: NOTIFICATION_TYPE_SUCCESS,
                                    message: 'Successfully deleted venus-grafana for: ' + team.name,
                                });
                            }
                        })
                        .finally(() => {
                            this.venusGrafanaLoading = false;
                        });
                }
            },
            {
                icon: 'sg-vrm-icon_055_cross-16px',
                extraText: this.team.name,
            },
        );
    }

    get canDelete() {
        return this.team && this.UserService.user && this.UserService.user.id === this.team.idOwner;
    }

    async onDelete() {
        if (this.busy) return;
        this.busy = true;
        const teamId = this.team.idUserGroup;
        const props = {
            item: this.$t('teams.confirm_deletion_item'),
            message: this.$t('teams.confirm_deletion_message'),
        };
        if (await this.VrmModalService.openVueModal(ConfirmDeletionModal, props)) {
            await this.UserGroupsService.deleteUserGroup(teamId);
            this.$router.push({ name: 'base.user-groups' });
        } else {
            this.busy = false;
        }
    }

    get ownerName() {
        if (!this.team) return '';
        const member = this.team.members.find(member => member.idUser === this.team.idOwner);
        return member ? member.name : '';
    }

    get localUserId() {
        const user = this.$store.state.UserService.user;
        return user ? user.id : 0;
    }

    get canMakeChanges() {
        return (
            this.team &&
            (this.team.idOwner === this.localUserId ||
                this.$rootScope.userState.isFullAdmin ||
                this.admins.some(admin => admin.idUser === this.localUserId))
        );
    }

    get isAdmin() {
        return this.$rootScope.userState.isAdmin;
    }

    get showVenusGrafanaButton() {
        return this.isAdmin || this.team.hasVenusGrafanaInstance;
    }

    get venusGrafanaDropdownOptions() {
        if (this.isAdmin) {
            return this.team.hasVenusGrafanaInstance
                ? [
                      this.DROPDOWN_ITEM_GO_TO_GRAFANA_INSTANCE,
                      this.DROPDOWN_ITEM_DELETE_GRAFANA_INSTANCE,
                  ]
                : [this.DROPDOWN_ITEM_CREATE_GRAFANA_INSTANCE];
        }

        return this.team.hasVenusGrafanaInstance ? [this.DROPDOWN_ITEM_GO_TO_GRAFANA_INSTANCE] : [];
    }

    canMakeSiteChanges(item) {
        return this.canMakeChanges || item.site.isAdmin;
    }

    canModifyMember(idMember) {
        return this.canMakeChanges || idMember === this.localUserId;
    }

    canRemoveMember(idMember) {
        return this.canModifyMember(idMember) && idMember !== this.team.idOwner;
    }

    async removeMember(idMember) {
        const isLeaving = idMember === this.localUserId;
        const props = {
            message: this.$t('teams.confirm_deletion_message_member'),
            action: this.$t('teams.confirm_remove_member_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            await this.UserGroupsService.removeUserFromGroup(this.team.idUserGroup, idMember);
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('users.user_removed_message'),
            });
            if (isLeaving) {
                this.UserGroupsService.reloadUserGroups();
                this.$router.push('base.user-groups');
            }
        }
    }

    async removeInvite(email) {
        const props = {
            message: this.$t('teams.confirm_deletion_message_pending_invitation'),
            action: this.$t('teams.confirm_remove_invite_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            await this.UserGroupsService.removeInvite(this.team.idUserGroup, email);
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('users.invite_removed_message'),
            });
        }
    }

    get canLinkMembers() {
        return this.canMakeChanges;
    }

    linkMembersPopup() {
        this.VrmModalService.openVueModal(UserGroupLinkUsersModal, { team: this.team });
    }

    get combinedSiteList() {
        const self = this;
        return [
            ...this.team.sites.map(site => ({
                key: 's' + site.idSite,
                title: site.description,
                isGroup: false,
                site,
                get accessLevelOption() {
                    return findAccessLevelOption(site.accessLevel);
                },
                set accessLevelOption(option) {
                    self.syncSiteControl(site, option.accessLevel);
                },
            })),
            ...this.team.siteGroups.map(group => ({
                key: 'g' + group.idSiteGroup,
                title: group.name,
                isGroup: true,
                group,
                get accessLevelOption() {
                    return findAccessLevelOption(group.accessLevel);
                },
                set accessLevelOption(option) {
                    self.syncGroupControl(group, option.accessLevel);
                },
            })),
        ].sort((a, b) => {
            return a.title.localeCompare(b.title);
        });
    }

    async syncSiteControl(site, accessLevel) {
        const originalAccessLevel = site.accessLevel;
        site.accessLevel = accessLevel;
        await this.UserGroupsService.setSiteAccessLevel(
            this.team.idUserGroup,
            site.idSite,
            accessLevel,
        ).catch(() => {
            site.accessLevel = originalAccessLevel;
        });
        this.UserGroupsService.reloadUserGroups();
    }

    async syncMembersControl(member, isAdmin) {
        member.isAdmin = isAdmin;
        try {
            await this.UserGroupsService.updateUserGroup(this.team.idUserGroup, {
                name: this.team.name,
                idOwner: this.team.idOwner,
                admins: this.team.members
                    .filter(member => member.isAdmin === 1)
                    .map(member => member.idUser),
            });
        } catch (e) {
            console.log('error', e);
        }
    }

    async unlinkSite(idSite) {
        const props = {
            message: this.$t('teams.confirm_deletion_message_installation'),
            action: this.$t('teams.confirm_unlink_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            this.UserGroupsService.unlinkSite(this.team.idUserGroup, idSite);
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('teams.installation_removed_message'),
            });
        }
    }

    syncGroupControl(group, accessLevel) {
        const originalAccessLevel = group.accessLevel;
        group.accessLevel = accessLevel;
        this.UserGroupsService.setSiteGroupAccessLevel(
            this.team.idUserGroup,
            group.idSiteGroup,
            accessLevel,
        ).catch(() => {
            group.accessLevel = originalAccessLevel;
        });
    }

    async unlinkSiteGroup(idSiteGroup) {
        const props = {
            message: this.$t('teams.confirm_deletion_message_installation'),
            action: this.$t('teams.confirm_unlink_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            this.UserGroupsService.unlinkSiteGroup(this.team.idUserGroup, idSiteGroup);
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('teams.installation_removed_message'),
            });
        }
    }

    linkSitesPopup() {
        this.VrmModalService.openVueModal(UserGroupLinkInstallationModal, { team: this.team });
    }

    linkSiteGroupsPopup() {
        this.VrmModalService.openVueModal(UserGroupLinkInstallationGroupsModal, {
            team: this.team,
        });
    }

    get formatListOfAdmins() {
        return this.admins.map(admin => admin.name).join(', ');
    }

    showRoleGuideModal() {
        this.VrmModalService.openVueModal(UserRolesGuideModal);
    }

    showTeamMembersRoleGuideModal() {
        this.VrmModalService.openVueModal(TeamMembersRolesGuideModal);
    }
}
</script>
